import React, { useState, useEffect, useContext } from 'react';
import '../view-archer-styles.css';

import { Alert, Avatar, Box, Button, Card, CardHeader, CardContent, Chip, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, List, ListItem, ListItemText, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material/';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import Badge, { BadgeProps } from '@mui/material/Badge';
import { styled} from '@mui/material/styles';
import { green, blue} from '@mui/material/colors';
import { Drafts as Draft, AttachMoney, Email as Sent, MoreHoriz, NotificationsNone, InsertCommentOutlined } from '@mui/icons-material/';

//components import
import NewArcherLetterDialog from './NewArcherLetterDialog';
import ArcherLetterEditor from './ArcherLetterEditor';
import ArcherIssueEditor from './ArcherIssueEditor';
import FilerHeaderBreadcrumbs from './FilerHeaderBreadcrumbs';
import FilerPayload from '../dataObjects/FilerPayload';
import ArcherLetterListing from '../dataObjects/ArcherLetterListing';
import ArcherIssueListing from '../dataObjects/ArcherIssueListing';
import ArcherIssueGalleryTemplate from '../dataObjects/ArcherIssueGalleryTemplate';
import { FilerCommunicationContext } from './FilersRoot';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';
import FilerGatewayResponses from './FilerGatewayResponses';
import FilerRefundTracker from './FilerRefundTracker';
import ArcherResponse from '../dataObjects/ArcherResponse';
import { AuditorCommunicationContext } from '../view-archer';

interface Props {
    displayStatus: string;
    issueTemplates?: ArcherIssueGalleryTemplate[];
    isPayloadUpdated: boolean;
}

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -3,
        top: 6,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 4px',
    },
}));


const FilerSummary = (props:Props) => {

    //initial state set up
    const context = useContext(FilerCommunicationContext);
    const auditorContext = useContext(AuditorCommunicationContext);

    const [letterId, setLetterId] = useState(0);
    const [issueId, setIssueId] = useState(0);

    const [filerPayload, setFilerPayload] = useState<FilerPayload>();
    const [filerCpfId, setFilerCpfId] = useState(0);
    const [filerIssues, setFilerIssues] = useState<ArcherIssueListing[]>();
    const [filerLetters, setFilerLetters] = useState<ArcherLetterListing[]>();

    const [isNewLetterDialogOpen, setNewLetterDialogOpen] = useState(false);
    const [isFilerSummaryOpen, setFilerSummaryOpen] = useState(true);
    const [isLetterEditorOpen, setLetterEditorOpen] = useState(false);
    const [isIssueEditorOpen, setIssueEditorOpen] = useState(false);
    const [isFilerGatewayResponsesOpen, setFilerGatewayResponsesOpen] = useState(false);
    const [isFilerRefundTrackerOpen, setFilerRefundTrackerOpen] = useState(false);
    const [skipResolvedIssues, setSkipResolvedIssues] = useState(false);
    const [skipResolvedLetters, setSkipResolvedLetters] = useState(false);

    let paths: React.SetStateAction<any[]> = [];
    const [filerHeaderPaths, setFilerHeaderPath] = useState(paths);

    const Item = styled(Paper)(({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        ...theme.typography.body2,
        padding: theme.spacing(1),
        marginBottom: 20,
        color: theme.palette.text.secondary,
    }));

    
    useEffect(() => {
        if (context.currentCpfId > 0 && props.displayStatus === "active") {
            getFilerPayload();
        };
    }, []);

    const getFilerPayload = async () => {
        let cpfId = context.currentCpfId;
        if (auditorContext.currentLetterCpfId > 0) {
            cpfId = auditorContext.currentLetterCpfId;
            context.currentCpfId = auditorContext.currentLetterCpfId;
        }

        await fetch(`/archer3/GetFilerPayload?cpfId=${cpfId}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetFilerPayload',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let payload = result.result;
                    setFilerPayload(payload);
                    setFilerCpfId(cpfId);
                    setFilerIssues(payload.issues);
                    setFilerLetters(payload.letters);
                    //default header path
                    paths = [{
                        name: `${payload.cpfId} ${payload.fullNameReverse}`,
                        status: '',
                        type: 'name'
                    }];
                    setFilerHeaderPath(paths);

                    //open letter directly
                    if (auditorContext.currentLetterId > 0) {
                        let letter = payload.letters.find((letter: ArcherLetterListing) => letter.id === auditorContext.currentLetterId);
                        if (letter) {
                            openLetterEditor(letter);
                            paths = [{
                                name: `${payload.cpfId} ${payload.fullNameReverse}`,
                                status: '',
                                type: 'name'
                            }];
                            paths.push(
                                {
                                    name: `${letter.date} ${letter.letterTemplateCode}`,
                                    status: `${letter.letterStatus}`,
                                    type: 'letter'
                                }
                            );
                            setFilerHeaderPath(paths);
                            auditorContext.currentLetterId = 0;
                        }
                    }
                    auditorContext.currentLetterCpfId = 0;
                }
            })
    }
        
    /*NEW LETTER*/
    const openNewLetterDialog = () => {
        setNewLetterDialogOpen(true);
    };

    const closeNewLetterDialog = (newLetterAdded: boolean) => {
        setNewLetterDialogOpen(false);
        if (newLetterAdded) {
            getFilerPayload();
        }
    };

    /*LETTER LIST*/
    const displayLetterStatusIcon = (status: string) => {
        switch (status) {
            case 'Draft':
                return (
                    <Avatar sx={{ bgcolor: '#ffc107' }} className="small-avatar">
                        <Draft fontSize="small" />
                    </Avatar>
                );
            case 'Sent':
                return (
                    <Avatar sx={{ bgcolor: green[500] }} className="small-avatar">
                        <Sent fontSize="small" />
                    </Avatar>
                );
            default:
                return '';
        }
    };

    const displayLetterHeader = (letter: ArcherLetterListing) => {
        return (
            <span><b>{letter.letterTemplateCode}</b> <i>{letter.date}</i></span>
        )
    };

    const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: 'white',
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: theme.typography.pxToRem(12),
            border: '1px solid #dadde9',
        },
    }));

    const displayIssueListDialog = (letter: ArcherLetterListing) => {
        return (
            <Grid container>
                <Typography variant="body2">
                    <List dense={true} sx={{ listStyleType: 'disc' }}>
                    {
                        letter.issues.map(
                            (issue) =>
                                <ListItem key={issue.issueId}>
                                    <ListItemText sx={{ display: 'list-item' }} className={issue.status}
                                        primary={`${issue.issueTypeCode} ${issue.header}`}
                                    />
                                </ListItem>
                        )
                    }
                    </List>
                </Typography>
            </Grid>
        )
    }

    const openLetterEditor = (letter: ArcherLetterListing) => {
        addPath(`${letter.date} ${letter.letterTemplateCode}`, `${letter.letterStatus}`, 'letter');
        setLetterId(letter.id);
        setLetterEditorOpen(true);
        setIssueEditorOpen(false);
        setFilerSummaryOpen(false); 
        setFilerGatewayResponsesOpen(false);
        setFilerRefundTrackerOpen(false);
    }

    const openLetterEditorByIssue = (issue: ArcherIssueListing) => {
        addPath(`${issue.date} AUDIT`, 'Sent', 'letter');
        setLetterId(issue.currentLetterId);
        setLetterEditorOpen(true);
        setIssueEditorOpen(false);
        setFilerSummaryOpen(false);
        setFilerGatewayResponsesOpen(false);
        setFilerRefundTrackerOpen(false);
    }

    const closeLetterEditor = () => {
        removePath('letter');
        getFilerPayload();
        setLetterEditorOpen(false);
        setIssueEditorOpen(false);
        setFilerSummaryOpen(true);
        setFilerGatewayResponsesOpen(false);
        setFilerRefundTrackerOpen(false);
    }

    const getFilerLetterListing = () => {

        setSkipResolvedLetters(!skipResolvedLetters);

        fetch(`/archer3/GetFilerLetterListings?cpfId=${filerCpfId}&skipResolved=${skipResolvedLetters}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetFilerLetterListings',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let letters = result.result;
                    setFilerLetters(letters);
                }
            })
    }

    /*ISSUE LIST*/

    const displayIssueStatusIcon = (status: string) => {
        let shortStatus = status;
        if (status.indexOf(",") > 0) {
            shortStatus = status.substring(0, status.indexOf(","));
        }
        
        switch (shortStatus) {
            case 'Delinquent':
                return (
                    <Chip variant="outlined" label="Delinquent" size="small" color="error" />
                );
            case 'Outstanding':
                return (
                    <Chip variant="outlined" label="Outstanding" size="small" color="error" />
                );
            case 'Held':
                return (
                    <Chip variant="outlined" label={status} size="small" color="primary" />
                );
            case 'No Response Required':
                return (
                    <Chip variant="outlined" label={status} size="small" color="success" />
                );
            case 'Punted':
                return (
                    <Chip variant="outlined" label={status} size="small" color="primary" />
                );
            case 'Referred':
                return (
                    <Chip variant="outlined" label={status} size="small" color="primary" />
                );
            case 'Resolved':
                return (
                    <Chip variant="outlined" label={status} size="small" color="success" />
                );
            default:
                return '';
        }
    }

    const openIssueEditor = (issue: ArcherIssueListing) => {
        addPath(`${issue.issueTypeCode} ${issue.header}`, `${issue.status}`, 'issue');
        setIssueId(issue.issueId);
        setIssueEditorOpen(true);
        setLetterEditorOpen(false);
        setFilerSummaryOpen(false);
        setFilerGatewayResponsesOpen(false);
        setFilerRefundTrackerOpen(false);
    }

    const closeIssueEditor = () => {
        removePath('issue');
        setIssueEditorOpen(false);
        if (filerHeaderPaths.find((path) => path.type === 'letter'))
            setLetterEditorOpen(true);
        else
            setFilerSummaryOpen(true);
    }

    const getFilerFullIssueListing = () => {

        setSkipResolvedIssues(!skipResolvedIssues);

        fetch(`/archer3/GetFilerIssueListings?cpfId=${filerCpfId}&skipResolved=${skipResolvedIssues}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetFilerIssueListings',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let issues = result.result;
                    setFilerIssues(issues);
                }
            })
    }

    /*Punt, Hold, Refer issues*/

    const [isProcessIssuesDialogOpen, setProcessIssueDialogOpen] = useState(false);
    const [holdDate, setHoldDate] = useState(new Date().toLocaleDateString('en-CA'));
    const [actionType, setActionType] = useState('');
    const [issueList, setIssueList] = useState('');
    
    let selectedIssueIds: number[] = new Array(); 
    const addIssueToIssueList = (event: any) => {
        let issueId = event.target.value;
        selectedIssueIds.push(issueId);
    }

    const openProcessIssuesDialog = (type: string) => {
        setIssueList(selectedIssueIds.toString());
        setProcessIssueDialogOpen(true);
        setActionType(type);
    };

    const closeProcessIssuesDialog = () => {
        setProcessIssueDialogOpen(false);
    };

    const processIssueList = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries((formData as any).entries());
        const note = formJson.note;
        const date = formJson.date;

        var params = [
            `date=${date}`,
            `note=${note}`,
            `issueIds=${issueList}`
        ].join('&');

        let url = `/Archer3/${actionType}Issues?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: `Error: ${actionType}Issues`,
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    selectedIssueIds = [];
                    closeProcessIssuesDialog();
                    getFilerPayload();
                }
            })
    }


    /*Filer Responses*/
    const openFilerResponses = () => {
        addPath(`Pending Responses`, ``, 'responses');
        setIssueEditorOpen(false);
        setLetterEditorOpen(false);
        setFilerSummaryOpen(false);
        setFilerRefundTrackerOpen(false);
        setFilerGatewayResponsesOpen(true);
    }

    const openFilerRefundTracker = () => {
        addPath(`Refund Tracker`, ``, 'tracker');
        setIssueEditorOpen(false);
        setLetterEditorOpen(false);
        setFilerSummaryOpen(false);
        setFilerGatewayResponsesOpen(false);
        setFilerRefundTrackerOpen(true);
    }

    /*breadcrumbs*/
    const addPath = (pathName: string, status: string, pathType: string) => {
        let path = {
            name: pathName,
            status: status,
            type: pathType
        }

        paths = [...filerHeaderPaths];
        paths.push(path);
        setFilerHeaderPath(paths);
    }

    const removePath = (pathType: string) => {
        paths = [...filerHeaderPaths];
        paths = paths.filter((path) => path.type !== pathType);
        setFilerHeaderPath(paths);
    }

    const linkFilerHome = () => {
        paths = [...filerHeaderPaths];
        paths = paths.filter((path) => path.type === 'name');
        setFilerHeaderPath(paths);
        setIssueEditorOpen(false);
        setLetterEditorOpen(false);
        setFilerSummaryOpen(true);
        setFilerGatewayResponsesOpen(false);
        setFilerRefundTrackerOpen(false);
        getFilerPayload();
    }

    const linkLetterEditor = () => {
        paths = [...filerHeaderPaths];
        paths = paths.filter((path) => path.type !== 'issue');
        setFilerHeaderPath(paths);
        setIssueEditorOpen(false);
        setLetterEditorOpen(true);
        setFilerSummaryOpen(false);
        setFilerGatewayResponsesOpen(false);
        setFilerRefundTrackerOpen(false);
    }

    const linkIssueEditor = () => {
        setIssueEditorOpen(true);
        setLetterEditorOpen(false);
        setFilerSummaryOpen(false);
        setFilerGatewayResponsesOpen(false);
        setFilerRefundTrackerOpen(false);
    }

    const linkFilerResponses = () => {
        setIssueEditorOpen(false);
        setLetterEditorOpen(false);
        setFilerSummaryOpen(false);
        setFilerGatewayResponsesOpen(true);
        setFilerRefundTrackerOpen(false);
    }

    const linkFilerRefundTracker = () => {
        setIssueEditorOpen(false);
        setLetterEditorOpen(false);
        setFilerSummaryOpen(false);
        setFilerGatewayResponsesOpen(false);
        setFilerRefundTrackerOpen(true);
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <div className="filer-container">
            <Box key={context.currentCpfId}>
                {/* breadcrumbs */}
                <Grid container className="filer-breadcrumbs">
                    {
                        isFilerSummaryOpen && filerPayload &&
                        <Grid item xs={12}>
                                <Item sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Grid container>
                                        <Grid item xs={3}></Grid>
                                        <Grid item xs={6} sx={{ textAlign: 'center' }}>
                                            <Typography variant="h6" sx={{ color: blue[500], fontWeight: 'bold' }}><Link href={`/Filers?q=${filerPayload.cpfId}`} target="_blank" underline="none">{filerPayload.cpfId} {filerPayload.fullNameReverse}</Link></Typography>
                                        </Grid>
                                        <Grid item xs={3} sx={{ textAlign: 'right' }}>
                                            <Button variant="text" startIcon={
                                                <StyledBadge badgeContent={filerPayload.pendingResponseCount} color="success" invisible={filerPayload.pendingResponseCount===0}>
                                                <NotificationsNone color="action" />
                                                </StyledBadge>
                                            } onClick={() => openFilerResponses()}>
                                                {filerPayload.pendingResponseCount === 0 && <i>Responses</i>}
                                                {filerPayload.pendingResponseCount> 0 && <i>&nbsp;&nbsp;&nbsp;New Response(s)</i>}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Item>
                        </Grid>
                    }

                    { !isFilerSummaryOpen &&
                        <Grid item xs={12}>
                            <Item><FilerHeaderBreadcrumbs paths={filerHeaderPaths} linkFilerHome={linkFilerHome} linkLetterEditor={linkLetterEditor} linkIssueEditor={linkIssueEditor} linkFilerResponses={linkFilerResponses} linkFilerRefundTracker={linkFilerRefundTracker}></FilerHeaderBreadcrumbs></Item>
                        </Grid>
                    }
                    
                </Grid>
                {/*filer summary*/}
                <Grid container className="filer-summary">
                    {
                        isFilerSummaryOpen &&
                        <>
                            {/* letters section */}
                            <Grid item xs={12}>
                                <Item>
                                    <Grid container>
                                        <Grid item xs={10} sx={{ textAlign: 'left' }}>
                                            <Typography className="section-title">LETTERS [{filerLetters && filerLetters.length}]</Typography>
                                        </Grid>
                                        {
                                            filerPayload &&
                                            <Grid item xs={2} sx={{ textAlign: 'right' }}>
                                                <Button variant="text" size="small" startIcon={<MoreHoriz />} onClick={getFilerLetterListing} >
                                                    <i>{skipResolvedLetters ? 'Load Outstanding Letters' : 'Load All Letters'}</i>
                                                </Button>
                                            </Grid>
                                        }
                                    </Grid>
                                    { /* <Divider sx={{ my: 1.5 }} /> */}
                                    <Grid container spacing={2} className="letter-list-container" sx={{ marginTop: 0.2, marginBottom: 1}}>
                                        {/*Create New Letter*/}
                                        <Grid item xs={3}>
                                            <Card variant="outlined">
                                                <CardHeader
                                                    avatar={
                                                        <Avatar sx={{ bgcolor: blue[500] }} className="small-avatar">
                                                            <Draft fontSize="small" />
                                                        </Avatar>
                                                    }
                                                    title={
                                                        <Alert icon={false} severity="info" className="is-clickable" onClick={openNewLetterDialog}>
                                                            Add New Letter
                                                        </Alert>
                                                    }
                                                />
                                            </Card>
                                            <NewArcherLetterDialog filerIssues={filerIssues} isNewLetterDialogOpen={isNewLetterDialogOpen} cpfId={context.currentCpfId} closeNewLetterDialog={closeNewLetterDialog} />
                                        </Grid>
                                        {/*Letter List*/}
                                        {
                                            filerLetters && filerLetters.map(
                                                (letter) =>
                                                <Grid item xs={3} key={letter.id}>
                                                    <Card variant="outlined" className="letter-card" onClick={()=>openLetterEditor(letter)}>
                                                            <CardHeader
                                                                avatar={displayLetterStatusIcon(letter.letterStatus)}
                                                                className="card-header"
                                                                title={displayLetterHeader(letter)}
                                                        />
                                                        <CardContent className="card-content">
                                                                <List dense={true}>
                                                                    {
                                                                        letter.issues.slice(0, 2).map(
                                                                            (issue) =>
                                                                                <ListItem key={issue.issueId}>
                                                                                    <ListItemText className={issue.status}
                                                                                        disableTypography
                                                                                        primary={`${issue.issueTypeCode} ${issue.header}`}
                                                                                    />
                                                                                </ListItem>
                                                                        )
                                                                    }
                                                                </List>
                                                                {
                                                                    letter.issues.length > 2 &&
                                                                    <Grid container justifyContent="left">
                                                                        <Grid item sx={{ marginLeft: 2 }}>
                                                                            <HtmlTooltip title={displayIssueListDialog(letter)} placement="top">
                                                                                <Typography variant="body2" color="primary"><i>{letter.issues.length - 2} more issues...</i></Typography>
                                                                            </HtmlTooltip>
                                                                        </Grid>
                                                                    </Grid>
                                                                }
                                                        </CardContent>
                                                    </Card>
                                                </Grid>    
                                            )
                                        }
                                    </Grid>
                                </Item>
                            </Grid>
                            {/* issues section */}
                            <Grid item xs={12}>
                                <Item>
                                    <Grid container>
                                        <Grid item xs={8} sx={{ textAlign: 'left' }}>
                                            <Stack direction="row" spacing={2}>
                                                <Typography className="section-title">OUTSTANDING ISSUES [{filerIssues && filerIssues.length}]</Typography>
                                                <Chip label='Hold' variant="outlined" color="warning" onClick={() => openProcessIssuesDialog('Hold')} />
                                                <Chip label='Punt' variant="outlined" color="error" onClick={() => openProcessIssuesDialog('Punt')} />
                                            </Stack>
                                        </Grid>
                                        {
                                            filerPayload &&
                                            <Grid item xs={4} sx={{ textAlign: 'right' }}>
                                                 <Button variant="text" size="small" startIcon={<MoreHoriz />} onClick={getFilerFullIssueListing} >
                                                    <i>{skipResolvedIssues? 'Load Outstanding Issues': 'Load All Issues' }</i>
                                                </Button>
                                            </Grid>
                                        }
                                    </Grid>
                                    {/*Issue List: table*/}
                                    <Grid container spacing={2} sx={{ marginTop: 1, marginBottom: 1 }}>
                                        {
                                            filerIssues && filerIssues.length === 0 &&
                                            <Alert severity="success" sx={{ margin: 2 }}>There are no outstanding issues!</Alert>
                                        }
                                        {
                                            filerIssues && filerIssues.length > 0 &&
                                            <TableContainer sx={{ maxHeight: 400, m: 1 }}>
                                                <Table stickyHeader size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell></TableCell>
                                                            <TableCell sx={{ textAlign: "left!important" }}>Audit Letter Date</TableCell>
                                                            <TableCell>Status</TableCell>
                                                            <TableCell sx={{ textAlign: "left!important" }}>Issue</TableCell>
                                                            <TableCell>Item Count</TableCell>
                                                            <TableCell>Days Open</TableCell>
                                                            <TableCell>Responses</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {
                                                            filerIssues && filerIssues.length > 0 &&
                                                            filerIssues.map((issue) =>
                                                                <TableRow
                                                                    key={issue.issueId}
                                                                >
                                                                    <TableCell>
                                                                        <Checkbox
                                                                            value={issue.issueId}
                                                                            onChange={addIssueToIssueList}
                                                                            size="small" sx={{ p: 0 }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Stack direction="row" spacing={1}>
                                                                            <Sent fontSize="small" color="success" />
                                                                            <Typography variant="body2" sx={{ color: blue[500] }} className="is-clickable" onClick={() => openLetterEditorByIssue(issue)}>{issue.date}</Typography>
                                                                        </Stack>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {displayIssueStatusIcon(issue.status)}
                                                                    </TableCell>
                                                                    <TableCell sx={{ textAlign: "left!important" }}>
                                                                        <Typography variant="body2" className="is-clickable" onClick={() => openIssueEditor(issue)}>{issue.issueTypeCode} {issue.header} </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {issue.flaggedItemCount}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {issue.daysOpen}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {
                                                                            issue.pendingResponseCount > 0 &&
                                                                            <StyledBadge badgeContent={issue.pendingResponseCount} color="primary">
                                                                                    <InsertCommentOutlined color="action"  className="is-clickable" onClick={() => openIssueEditor(issue)} />
                                                                            </StyledBadge>
                                                                        }

                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        }
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        }
                                        
                                    </Grid>
                                </Item>
                            </Grid>
                            {/* refund section */}
                            <Grid item xs={12}>
                                <Item>
                                    <Grid container>
                                        <Grid item xs={6} sx={{ textAlign: 'left' }}>
                                            <Stack direction="row" spacing={2}>
                                                <Typography className="section-title">PENDING REFUNDS {filerPayload && filerPayload.refundRelatedResponses.length > 0 ? `[${filerPayload.refundRelatedResponses.length}]` : ''} </Typography>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={6} sx={{ textAlign: 'right' }}>

                                            <Button variant="text" startIcon={
                                                <StyledBadge badgeContent={filerPayload && filerPayload.refundRelatedResponses.length} color="success" invisible={filerPayload && filerPayload.refundRelatedResponses.length===0}>
                                                    <AttachMoney color="action" />
                                                </StyledBadge>
                                            } onClick={() => openFilerRefundTracker()}>
                                                <i>{filerPayload && filerPayload.refundRelatedResponses.length>0 && <span>&nbsp;&nbsp;&nbsp;</span>}Go to Tracker
                                                </i>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2} sx={{ marginTop: 1, marginBottom: 1 }}>
                                        {
                                            filerPayload && filerPayload.refundRelatedResponses.length === 0 &&
                                            <Alert severity="success" sx={{ margin: 2 }}>There are no pending refund related responses!</Alert>
                                        }
                                        {
                                            filerPayload && filerPayload.refundRelatedResponses.length > 0 &&
                                            <TableContainer sx={{ maxHeight: 300, m: 1 }}>
                                                <Table stickyHeader size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Status</TableCell>
                                                            <TableCell>Type</TableCell>
                                                            <TableCell>Date</TableCell>
                                                            <TableCell>Name</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {
                                                            filerPayload &&
                                                            filerPayload.refundRelatedResponses.map((refund: ArcherResponse) =>
                                                                <TableRow
                                                                    key={refund.id}
                                                                >
                                                                    <TableCell>
                                                                        <Chip label={'Unlinked'} color="warning" size="small" variant="outlined" />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {refund.responseTypeDescription}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {refund.responseDate}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {refund.groupUi}
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        }
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        }
                                        
                                    </Grid>
                                </Item>
                            </Grid>
                        </>
                    }
                </Grid>
                {/*letter editor*/}
                <Grid container className="letter-editor">
                    {
                        isLetterEditorOpen &&
                        <Grid item xs={12}>
                            <Item>
                                    <ArcherLetterEditor letterId={letterId} closeLetterEditor={closeLetterEditor} openIssueEditor={openIssueEditor}></ArcherLetterEditor>
                            </Item>
                        </Grid>
                    }
                   
                </Grid>
                {/*issue editor*/}
                <Grid container className="issue-editor">
                    {
                        isIssueEditorOpen &&
                        <Grid item xs={12}>
                            <Item>
                                    <ArcherIssueEditor issueTemplates={props.issueTemplates} issueId={issueId} closeIssueEditor={closeIssueEditor}></ArcherIssueEditor>
                            </Item>
                        </Grid>
                    }
                </Grid>
                {/*filer responses*/}
                <Grid container className="gateway-responses-editor">
                    {
                        isFilerGatewayResponsesOpen &&
                        <Grid item xs={12}>
                            <Item>
                                    <FilerGatewayResponses cpfId={context.currentCpfId} ></FilerGatewayResponses>
                            </Item>
                        </Grid>
                    }
                </Grid>
                {/* punt, hold, refer issues */}
                <Dialog
                    open={isProcessIssuesDialogOpen}
                    onClose={closeProcessIssuesDialog}
                    PaperProps={{
                        component: 'form',
                        onSubmit: (event: React.FormEvent<HTMLFormElement>)=>processIssueList(event)
                    }}
                >
                    <DialogTitle>Issues {actionType} Date...</DialogTitle>
                    <DialogContent>
                        <TextField           
                            required
                            margin="dense"
                            type="date"
                            name="date"
                            defaultValue={holdDate}
                            fullWidth
                            variant="standard"
                        />
                        <TextField
                            margin="dense"
                            name="note"
                            label="Leave a note (optional)"
                            fullWidth
                            variant="standard"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeProcessIssuesDialog}>Cancel</Button>
                        <Button type="submit">Save</Button>
                    </DialogActions>
                </Dialog>
                {/*filer refund tracker*/}
                <Grid container className="filer-refund-tracker-editor">
                    {
                        isFilerRefundTrackerOpen &&
                        <Grid item xs={12}>
                            <Item>
                                <FilerRefundTracker cpfId={context.currentCpfId} ></FilerRefundTracker>
                            </Item>
                        </Grid>
                    }
                </Grid>
            </Box>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </div>
    )
}

export default FilerSummary